import clsx from 'clsx';
import React, {
  Suspense, useCallback, useEffect, useId, useMemo, useRef, useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Loader } from '@/components/loader/loader';
import ModelContext from '@/components/modelContext/modelContext';
import PlaygroundChat from '@/components/playgroundChat/playgroundChat';
import { PlaygroundInfo, PlaygroundType } from '@/components/playgroundHeader/playgroundHeader';
import { useStreamingParser } from '@/hooks/useStreamingParser';
import { ChatMsgBasic, ChatMsgBasicHistory, ChatRoleEnum } from '@/types/chat';
import {
  ModelGenerationParamsCustom, ModelGenerationParamsDefault, ModelInvokeArgs, ModelInvokeStreamChunk
} from '@/types/model';
import { pollModelReady } from '@/utils/pollModelReady';
import { parseParams } from '@/utils/searchParams';

import styles from './page.module.scss';


type SearchParams = {
  mn: string;
};

function ModelPlaygroundPage() {
  const id = useId();
  const [ searchParams ] = useSearchParams();
  const {
    mn: qModelName
  } = parseParams<SearchParams>( searchParams );

  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgBasicHistory>( [] );
  const [ sGenerationParamsCustom, _setSGenerationParamsCustom ] = useState<ModelGenerationParamsCustom>( {} );
  const [ sGenerationParamsDefault, setSGenerationParamsDefault ] = useState<ModelGenerationParamsDefault>( {} );
  const [ sInput, setSInput ] = useState( '' );
  const [ sIsLoading, setSIsLoading ] = useState<boolean>( true );
  const [ sIsWaiting, setSIsWaiting ] = useState<boolean>( false );
  const [ sModelName ] = useState<string>( qModelName );
  const [ sPrompt, setSPrompt ] = useState<string>( '' );

  const abortControllerRef = useRef<AbortController | null>( null );
  const assistantMessageIndexRef = useRef<number | null>( null );
  const inputRef = useRef<string>( '' );
  const messagesEndRef = useRef<null | HTMLDivElement>( null );

  useEffect( () => {
    setSIsLoading( false );
  }, [ qModelName ] );

  useEffect( () => {
    inputRef.current = sInput;
  }, [ sInput ] );

  const parseModelChunk = useCallback( ( chunk: unknown ) => {
    const typedChunk = chunk as ModelInvokeStreamChunk;

    if( typedChunk.error ) {
      return { error: typedChunk.message || typedChunk.error, shouldUpdate: false };
    }

    const content = typedChunk.chunk?.choices?.[0]?.delta?.content;
    return {
      content: content || '',
      shouldUpdate: !!content
    };
  }, [] );

  const handleUpdateMessage = useCallback( ( accumulatedContent: string, isFirstChunk: boolean ) => {
    const assistantMsg: ChatMsgBasic = {
      role: ChatRoleEnum.ASSISTANT,
      content: accumulatedContent
    };

    setSChatMsgHistory( prevHistory => {
      const updatedHistory = [ ...prevHistory ];

      if( isFirstChunk ) {
        // First chunk - add new message
        updatedHistory.push( assistantMsg );
        assistantMessageIndexRef.current = updatedHistory.length - 1;
      } else if( assistantMessageIndexRef.current !== null ) {
        // Subsequent chunks - update existing message
        updatedHistory[assistantMessageIndexRef.current] = assistantMsg;
      }

      return updatedHistory;
    } );

    setTimeout( () => {
      messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
    }, 100 );
  }, [] );

  const { processStream, abort } = useStreamingParser( {
    parseChunk: parseModelChunk,
    onUpdateMessage: handleUpdateMessage,
    onError: ( error ) => {
      console.error( error.message );
      toast.error( error.message );
    },
    onAbort: () => {
      setSIsWaiting( false );
    }
  } );

  const handleStop = useCallback( () => {
    if( abortControllerRef.current ) {
      abortControllerRef.current.abort();
    }
    abort();
  }, [ abort ] );

  const onClickChat = useCallback( async() => {
    setSIsWaiting( true );
    assistantMessageIndexRef.current = null;

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const newChatMsgHistory: ChatMsgBasicHistory = [
        ...sChatMsgHistory
      ];

      // for new conversations push prompt first if provided
      if( sPrompt.length && !sChatMsgHistory.length ) {
        const promptMsg: ChatMsgBasic = {
          role: ChatRoleEnum.DEVELOPER,
          content: sPrompt
        };
        newChatMsgHistory.push( promptMsg );
      }

      const newInputMessage: ChatMsgBasic = {
        role: ChatRoleEnum.USER,
        content: inputRef.current
      };
      newChatMsgHistory.push( newInputMessage );

      setSChatMsgHistory( newChatMsgHistory );

      setTimeout( ()=>{
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      setSInput( '' );

      await pollModelReady( sModelName );

      const args: ModelInvokeArgs = {
        conversation: newChatMsgHistory,
        generationParamsCustom: sGenerationParamsCustom,
        generationParamsDefault: sGenerationParamsDefault,
        modelName: sModelName
      };

      const stream = await APIGateway.models.modelInvokeStream( args, abortControllerRef.current.signal );
      await processStream( stream );
    } catch( e ) {
      const error = e as Error;
      // Don't show error toast if the request was aborted by user
      if( error.name !== 'AbortError' ) {
        console.error( error.message );
        toast.error( error.message );
      }
    }

    setSIsWaiting( false );
    abortControllerRef.current = null;
  }, [ sChatMsgHistory, sPrompt, sGenerationParamsCustom, sGenerationParamsDefault, sModelName, processStream ] );

  const onClickReset = useCallback( () => setSChatMsgHistory( [] ), [] );

  const handleTemperatureChange = useCallback( ( value: number ) => {
    const nextParams = !value ?
        { ...sGenerationParamsDefault } : {
          ...sGenerationParamsDefault,
          temperature: value
        };

    setSGenerationParamsDefault( nextParams );
  }, [ sGenerationParamsDefault ] );

  const memoizedMessages = useMemo( () => sChatMsgHistory.map( x => ( { role: x.role, text: x.content } ) ), [ sChatMsgHistory ] );

  return (
    <div className={ clsx( 'page', 'no_overflow' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : (
              <div className={ clsx( 'layout__col', 'overflow_hide' ) }>
                <div className={ clsx( 'layout__row', styles.playground_type ) }>
                  <PlaygroundType label="LiteLLM Model" />
                </div>
                <div className={ clsx( 'layout__row', styles.playground_info ) }>
                  <PlaygroundInfo
                    name={ sModelName }
                    onReset={ onClickReset }
                    backHref="/models"
                  />
                </div>
                <div className={ clsx( 'layout__row', 'overflow_hide', styles.playground_chat ) }>
                  <div className={ clsx( 'layout__col', styles.chat_context ) }>
                    <ModelContext
                      prompt={ sPrompt }
                      temperature={ sGenerationParamsDefault.temperature || 0 }
                      disabled={ !!sChatMsgHistory.length }
                      onPromptChange={ setSPrompt }
                      onTemperatureChange={ handleTemperatureChange }
                    />
                  </div>
                  <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_convo ) }>
                    <PlaygroundChat
                      id={ id }
                      enableStructured={ false }
                      input={ sInput }
                      isWaiting={ sIsWaiting }
                      messages={ memoizedMessages }
                      messagesEndRef={ messagesEndRef }
                      onClickChat={ onClickChat }
                      onClickStop={ handleStop }
                      onInputChange={ setSInput }
                    />
                  </div>
                </div>
              </div>
            )
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ModelPlaygroundPage />
    </Suspense>
  );
}
